import {
  type JSXElement,
  type ParentComponent,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  useContext,
} from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";

// Navigation pane state machine
export const NavigationPaneMode = {
  COLLAPSED: "collapsed", // Panel hidden, thin strip visible
  EXPANDED: "expanded", // Panel fully visible
} as const;

export type NavigationPaneMode = (typeof NavigationPaneMode)[keyof typeof NavigationPaneMode];

// Chat state machine for prompt submission and layout transitions
export const ChatLayoutState = {
  INITIAL: "initial", // Pre-prompt state with centered layout // initial
  ACTIVE: "active", // Post-prompt state with active chat layout
  STREAMING: "streaming", // Currently receiving AI response
  ERROR: "error", // Error state requiring user intervention
} as const;

export type ChatLayoutState = (typeof ChatLayoutState)[keyof typeof ChatLayoutState];

// Extended pane state machine with enhanced modes
export const ExtendedPaneMode = {
  HIDDEN: "hidden", // Panel is not visible
  PREVIEW: "preview", // Panel is visible but collapsed/minimized
  ACTIVE: "active", // Panel is fully expanded and interactive
  FLOATING: "floating", // Panel is detached from main layout
} as const;

export type ExtendedPaneMode = (typeof ExtendedPaneMode)[keyof typeof ExtendedPaneMode];

// Prompt bar mode state machine
export const PromptBarMode = {
  NORMAL: "normal", // Standard prompt input mode
  DROPZONE: "dropzone", // File upload dropzone mode
  KNOWLEDGE_MENU: "knowledge_menu", // Knowledge sources menu open
} as const;

export type PromptBarMode = (typeof PromptBarMode)[keyof typeof PromptBarMode];

// Interaction mode for the prompt bar
export const InteractionMode = {
  CHAT: "chat", // Standard chat interaction
  AGENT: "agent", // Agent-based interaction
  WORKFLOW: "workflow", // Workflow automation
} as const;

export type InteractionMode = (typeof InteractionMode)[keyof typeof InteractionMode];

export interface NavigationPaneState {
  mode: NavigationPaneMode;
  width: number;
  secondaryPane: "assets" | "concepts" | null;
  userHasManuallySetPreference: boolean; // Track if user has manually docked/undocked
}

// Computed helpers
export const isExpanded = (state: NavigationPaneState): boolean => state.mode === NavigationPaneMode.EXPANDED;

// Chat state helpers
export const isPromptSubmitted = (state: ChatState): boolean => state.layoutState !== ChatLayoutState.INITIAL;

export const isStreamingEnabled = (state: ChatState): boolean => state.layoutState === ChatLayoutState.STREAMING;

export const canAcceptNewPrompt = (state: ChatState): boolean =>
  state.layoutState === ChatLayoutState.ACTIVE || state.layoutState === ChatLayoutState.ERROR;

// Extended pane helpers
export const isExtendedPaneVisible = (state: ExtendedPaneState): boolean => state.mode !== ExtendedPaneMode.HIDDEN;

export const isExtendedPaneInteractive = (state: ExtendedPaneState): boolean =>
  state.mode === ExtendedPaneMode.ACTIVE || state.mode === ExtendedPaneMode.FLOATING;

export interface ChatState {
  layoutState: ChatLayoutState;
  isStreamingActive: boolean;
  messageCount: number;
}

export interface PromptBarState {
  mode: PromptBarMode;
  interactionMode: InteractionMode;
  knowledgeSources: {
    training: boolean;
    web: boolean;
    myKnowledge: boolean;
    memories: boolean;
  };
}

export const ExtendedPaneContentType = {
  ARTIFACT: "artifact",
  INTERSTITIAL: "interstitial",
  INTERSTITIAL_GENERATING: "interstitial_generating",
  CONCEPTS_POWERING_CHAT: "concepts_powering_chat",
} as const;

export type ExtendedPaneContentType = (typeof ExtendedPaneContentType)[keyof typeof ExtendedPaneContentType];

export interface ExtendedPaneContentData {
  operationId: string;
  title?: string;
  type: ExtendedPaneContentType;
}

// Legacy interface for backwards compatibility - now includes type
export interface ArtifactData {
  operationId: string;
  blockId?: string; // Optional block ID for block-specific content
  title?: string;
  type?: ExtendedPaneContentType; // Type field for routing to correct viewer
  editMode?: boolean; // Whether to open the artifact in edit mode
}

export interface ExtendedPaneState {
  mode: ExtendedPaneMode;
  position: "left" | "right";
  width: number;
  hasBeenManuallyResized: boolean;
  content?: JSXElement; // Content component to render in the pane
  // Single active artifact instead of multiple tabs
  activeArtifact?: ArtifactData;
  conceptsPoweringChat: boolean;
}

export interface AppUIState {
  navigationPane: NavigationPaneState;
  extendedPane: ExtendedPaneState;
  chat: ChatState;
  promptBar: PromptBarState;
  isDebugMode: boolean;
  useRichTextReplacement: boolean;
  enableAutoCollapseOperations: boolean;
}

const MIN_NAV_WIDTH = 250;
const MIN_VIEWPORT_FOR_RESIZE = 640; // Minimum viewport width to enable panel resizing
const MIN_ARTIFACT_PANEL_WIDTH = 500; // Minimum width for artifact panel to display all controls properly

export interface AppUIContextType {
  state: AppUIState;
  isHydrated: () => boolean;
  actions: {
    // Navigation pane actions
    toggleNavigationPane: () => void;
    setNavigationPaneMode: (mode: NavigationPaneMode) => void;
    setNavigationPaneModeForRoute: (isHomepage: boolean) => void;
    setNavigationPaneWidth: (width: number) => void;

    openSecondaryPane: (pane: "assets" | "concepts") => void;
    closeSecondaryPane: () => void;

    // Chat state actions
    submitPrompt: () => void;
    startStreaming: () => void;
    stopStreaming: () => void;
    setChatError: () => void;
    resetChat: () => void;
    incrementMessageCount: () => void;

    // Extended pane actions
    setExtendedPaneMode: (mode: ExtendedPaneMode) => void;
    setExtendedPanePosition: (position: "left" | "right") => void;
    setExtendedPaneWidth: (width: number, isManualResize?: boolean) => void;
    setExtendedPaneContent: (content: JSXElement) => void;
    setExtendedPaneConceptsPowering: (show: boolean) => void;
    clearExtendedPaneContent: () => void;
    setExtendedPaneArtifact: (operationId: string, title?: string, blockId?: string, editMode?: boolean) => void;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneInterstitial: (operationId: string, chat: any, title?: string, blockId?: string) => void;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    setExtendedPaneInterstitialGenerating: (operationId: string, chat: any, title?: string, blockId?: string) => void;
    clearExtendedPaneArtifact: () => void;
    toggleExtendedPane: () => void;

    // Prompt bar actions
    setPromptBarMode: (mode: PromptBarMode) => void;
    setInteractionMode: (mode: InteractionMode) => void;
    toggleKnowledgeSource: (source: keyof PromptBarState["knowledgeSources"]) => void;
    resetPromptBar: () => void;

    // Debug actions
    toggleDebugMode: () => void;
    toggleRichTextReplacement: () => void;
    toggleAutoCollapseOperations: () => void;
  };
}

const AppUIContext = createContext<AppUIContextType>();

const DEFAULT_STATE: AppUIState = {
  navigationPane: {
    mode: NavigationPaneMode.COLLAPSED,
    width: MIN_NAV_WIDTH,
    secondaryPane: null,
    userHasManuallySetPreference: false,
  },
  extendedPane: {
    mode: ExtendedPaneMode.HIDDEN,
    position: "right",
    width: MIN_NAV_WIDTH,
    hasBeenManuallyResized: false,
    content: undefined,
    activeArtifact: undefined,
    conceptsPoweringChat: false,
  },
  chat: {
    layoutState: ChatLayoutState.INITIAL,
    isStreamingActive: false,
    messageCount: 0,
  },
  promptBar: {
    mode: PromptBarMode.NORMAL,
    interactionMode: InteractionMode.CHAT,
    knowledgeSources: {
      training: true,
      web: true,
      myKnowledge: true,
      memories: true,
    },
  },
  isDebugMode: false,
  useRichTextReplacement: true, // Default to using rich text replacement
  enableAutoCollapseOperations: true, // Default to enabling auto-collapse
};

// LocalStorage key for persistence
const UI_STATE_LOCALSTORAGE_KEY = "storytell-ui-state";

// Helper functions for localStorage management
const getLocalStorageValue = (key: string): string | null => {
  if (isServer) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setLocalStorageValue = (key: string, value: string): void => {
  if (isServer) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Handle quota exceeded or other localStorage errors
    console.warn("Failed to save UI state to localStorage");
  }
};

const loadStateFromLocalStorage = (): Partial<AppUIState> => {
  const storedValue = getLocalStorageValue(UI_STATE_LOCALSTORAGE_KEY);
  if (!storedValue) return {};

  try {
    return JSON.parse(storedValue);
  } catch {
    return {};
  }
};

const saveStateToLocalStorage = (state: AppUIState): void => {
  // Only persist certain state properties, not extended pane mode or chat state
  const persistedState = {
    navigationPane: {
      mode: state.navigationPane.mode,
      width: state.navigationPane.width,
      secondaryPane: state.navigationPane.secondaryPane,
      userHasManuallySetPreference: state.navigationPane.userHasManuallySetPreference,
    },
    extendedPane: {
      position: state.extendedPane.position,
      width: state.extendedPane.width,
      hasBeenManuallyResized: state.extendedPane.hasBeenManuallyResized,
      // Exclude mode from persistence - always start hidden
    },
    promptBar: {
      knowledgeSources: state.promptBar.knowledgeSources,
      // Don't persist mode or interactionMode - always start fresh
    },
    // Persist debug flags
    isDebugMode: state.isDebugMode,
    useRichTextReplacement: state.useRichTextReplacement,
    enableAutoCollapseOperations: state.enableAutoCollapseOperations,
    // Don't persist chat state - always start fresh
  };
  setLocalStorageValue(UI_STATE_LOCALSTORAGE_KEY, JSON.stringify(persistedState));
};

export const AppUIContextProvider: ParentComponent = (props) => {
  // Always start with default state for SSR consistency
  const [state, setState] = createStore<AppUIState>(DEFAULT_STATE);
  const [isHydrated, setIsHydrated] = createSignal(false);

  // Load from localStorage only after hydration
  createEffect(() => {
    if (!isServer && !isHydrated()) {
      const savedState = loadStateFromLocalStorage();
      if (Object.keys(savedState).length > 0) {
        // Merge saved state but ensure extended pane mode starts as HIDDEN and chat state starts fresh
        const { extendedPane: savedExtendedPane, navigationPane: savedNavigationPane, ...otherSavedState } = savedState;

        // Migrate old navigation pane modes (undocked/docked) to new modes (collapsed/expanded)
        const cleanNavigationPane = savedNavigationPane
          ? {
              // Migrate old mode values to new enum
              // Cast to string since localStorage could contain old values like "docked" or "undocked"
              mode:
                (savedNavigationPane.mode as string) === "docked" || (savedNavigationPane.mode as string) === "expanded"
                  ? NavigationPaneMode.EXPANDED
                  : NavigationPaneMode.COLLAPSED,
              width: savedNavigationPane.width || DEFAULT_STATE.navigationPane.width,
              secondaryPane: savedNavigationPane.secondaryPane || null,
              userHasManuallySetPreference: savedNavigationPane.userHasManuallySetPreference || false,
            }
          : DEFAULT_STATE.navigationPane;

        // Clean state - ensure extended pane always starts hidden and chat starts fresh
        const cleanExtendedPane = savedExtendedPane
          ? {
              mode: ExtendedPaneMode.HIDDEN, // Always start hidden
              position: savedExtendedPane.position || DEFAULT_STATE.extendedPane.position,
              width: savedExtendedPane.width || DEFAULT_STATE.extendedPane.width,
              hasBeenManuallyResized: savedExtendedPane.hasBeenManuallyResized || false,
              content: undefined, // Always start with no content
              activeArtifact: undefined, // Always start with no active artifact
              conceptsPoweringChat: false, // Always start with no concepts powering chat
            }
          : DEFAULT_STATE.extendedPane;

        // Clean prompt bar state - preserve knowledge sources but reset mode and interaction
        const cleanPromptBar = otherSavedState.promptBar
          ? {
              mode: PromptBarMode.NORMAL, // Always start with normal mode
              interactionMode: InteractionMode.CHAT, // Always start with chat mode
              knowledgeSources: {
                training: otherSavedState.promptBar.knowledgeSources?.training ?? true,
                web: otherSavedState.promptBar.knowledgeSources?.web ?? true,
                myKnowledge: otherSavedState.promptBar.knowledgeSources?.myKnowledge ?? true,
                memories: otherSavedState.promptBar.knowledgeSources?.memories ?? true,
              },
            }
          : DEFAULT_STATE.promptBar;

        const cleanState = {
          navigationPane: cleanNavigationPane, // Use migrated navigation pane state
          extendedPane: cleanExtendedPane,
          chat: DEFAULT_STATE.chat, // Always start with fresh chat state
          promptBar: cleanPromptBar, // Preserve knowledge sources, reset modes
          isDebugMode: otherSavedState.isDebugMode ?? DEFAULT_STATE.isDebugMode, // Restore debug mode
          useRichTextReplacement: otherSavedState.useRichTextReplacement ?? DEFAULT_STATE.useRichTextReplacement, // Restore rich text flag
          enableAutoCollapseOperations:
            otherSavedState.enableAutoCollapseOperations ?? DEFAULT_STATE.enableAutoCollapseOperations, // Restore auto-collapse flag
        };

        setState(cleanState);

        // Immediately save the cleaned state to update localStorage
        saveStateToLocalStorage(cleanState);
      }
      setIsHydrated(true);
    }
  });

  // Save state to localStorage whenever it changes (but only after hydration)
  createEffect(() => {
    if (!isServer && isHydrated()) {
      saveStateToLocalStorage(state);
    }
  });

  // Force navigation pane to collapse on small viewports (< 640px)
  createEffect(() => {
    if (isServer) return;

    const handleViewportResize = () => {
      const viewportWidth = window.innerWidth;

      // On small viewports, force navigation pane to collapse
      if (viewportWidth < MIN_VIEWPORT_FOR_RESIZE) {
        if (state.navigationPane.mode === NavigationPaneMode.EXPANDED) {
          setState("navigationPane", "mode", NavigationPaneMode.COLLAPSED);
        }
      }
    };

    // Initial check
    handleViewportResize();

    // Listen for viewport resize
    window.addEventListener("resize", handleViewportResize);

    onCleanup(() => {
      window.removeEventListener("resize", handleViewportResize);
    });
  });

  const actions: AppUIContextType["actions"] = {
    // Navigation pane actions
    toggleNavigationPane: () => {
      const isCollapsed = state.navigationPane.mode === NavigationPaneMode.COLLAPSED;
      setState("navigationPane", "mode", isCollapsed ? NavigationPaneMode.EXPANDED : NavigationPaneMode.COLLAPSED);

      // Mark that user has manually set a preference
      setState("navigationPane", "userHasManuallySetPreference", true);
    },

    setNavigationPaneMode: (mode: NavigationPaneMode) => {
      setState("navigationPane", "mode", mode);
      if (mode !== NavigationPaneMode.EXPANDED) {
        setState("navigationPane", "secondaryPane", null);
      }
    },

    // Auto-set navigation pane mode based on homepage detection
    setNavigationPaneModeForRoute: (isHomepage: boolean) => {
      // If user has manually set a preference, respect it
      if (state.navigationPane.userHasManuallySetPreference) {
        return;
      }

      // Auto-set based on route
      if (isHomepage) {
        // Homepage: default to COLLAPSED
        setState("navigationPane", "mode", NavigationPaneMode.COLLAPSED);
      } else {
        // Non-homepage: default to EXPANDED
        setState("navigationPane", "mode", NavigationPaneMode.EXPANDED);
      }
    },

    setNavigationPaneWidth: (width: number) => {
      // Spec: up to 50% of viewport width, but never less than 300px
      if (isServer) return;
      const maxWidth = Math.max(MIN_NAV_WIDTH, window.innerWidth * 0.5);
      setState("navigationPane", "width", Math.max(MIN_NAV_WIDTH, Math.min(maxWidth, width)));
    },

    // Chat state actions
    submitPrompt: () => {
      setState("chat", {
        layoutState: ChatLayoutState.ACTIVE,
        messageCount: state.chat.messageCount + 1,
      });
    },

    startStreaming: () => {
      setState("chat", {
        layoutState: ChatLayoutState.STREAMING,
        isStreamingActive: true,
      });
    },

    stopStreaming: () => {
      setState("chat", {
        layoutState: ChatLayoutState.ACTIVE,
        isStreamingActive: false,
      });
    },

    setChatError: () => {
      setState("chat", {
        layoutState: ChatLayoutState.ERROR,
        isStreamingActive: false,
      });
    },

    resetChat: () => {
      setState("chat", DEFAULT_STATE.chat);
    },

    incrementMessageCount: () => {
      setState("chat", "messageCount", state.chat.messageCount + 1);
    },

    // Extended pane actions
    setExtendedPaneMode: (mode: ExtendedPaneMode) => {
      setState("extendedPane", "mode", mode);
    },

    toggleExtendedPane: () => {
      const currentMode = state.extendedPane.mode;
      const newMode = currentMode === ExtendedPaneMode.HIDDEN ? ExtendedPaneMode.ACTIVE : ExtendedPaneMode.HIDDEN;
      setState("extendedPane", "mode", newMode);
    },

    setExtendedPanePosition: (position: "left" | "right") => {
      setState("extendedPane", "position", position);
    },

    setExtendedPaneWidth: (width: number, isManualResize = false) => {
      // Constraints: min 500px (to show all buttons), max 50% of viewport width
      if (isServer) return;

      // Use full viewport width for calculations (decoupled from NavigationPane)
      // The ExtendedPane overlays or the margin system handles positioning
      const viewportWidth = window.innerWidth;

      // On small viewports (< 640px), use fixed 90% width and ignore manual resize
      if (viewportWidth < MIN_VIEWPORT_FOR_RESIZE) {
        setState("extendedPane", "width", viewportWidth * 0.9);
        return;
      }

      // Calculate constraints based on viewport, not available space
      // Minimum: 500px to ensure all UI controls fit properly
      // Maximum: 50% of viewport width
      const minWidth = MIN_ARTIFACT_PANEL_WIDTH;
      const maxWidth = viewportWidth * 0.5;

      // Ensure we have valid bounds
      const finalWidth = Math.max(minWidth, Math.min(maxWidth, width));
      setState("extendedPane", "width", finalWidth);

      // Mark as manually resized if this was a user action
      if (isManualResize) {
        setState("extendedPane", "hasBeenManuallyResized", true);
      }
    },

    setExtendedPaneContent: (content: JSXElement) => {
      setState("extendedPane", "content", content);
    },

    clearExtendedPaneContent: () => {
      setState("extendedPane", "content", undefined);
    },

    setExtendedPaneArtifact: (operationId: string, title?: string, blockId?: string, editMode?: boolean) => {
      const artifact: ArtifactData = {
        operationId,
        blockId,
        title,
        type: ExtendedPaneContentType.ARTIFACT,
        editMode,
      };
      setState("extendedPane", "conceptsPoweringChat", false);
      setState("extendedPane", "activeArtifact", artifact);
      setState("extendedPane", "content", undefined);
    },

    setExtendedPaneConceptsPowering: (show: boolean) => {
      if (show) {
        setState("extendedPane", "activeArtifact", undefined);
        setState("extendedPane", "content", undefined);
        setState("extendedPane", "conceptsPoweringChat", show);
        setState("extendedPane", "mode", ExtendedPaneMode.ACTIVE);
      } else {
        setState("extendedPane", "conceptsPoweringChat", show);
      }
    },

    setExtendedPaneInterstitial: (operationId: string, title?: string, blockId?: string) => {
      const artifact: ArtifactData = {
        operationId,
        blockId,
        title,
        type: ExtendedPaneContentType.INTERSTITIAL,
      };
      setState("extendedPane", "activeArtifact", artifact);
      setState("extendedPane", "content", undefined);
    },

    setExtendedPaneInterstitialGenerating: (operationId: string, title?: string, blockId?: string) => {
      const artifact: ArtifactData = {
        operationId,
        blockId,
        title,
        type: ExtendedPaneContentType.INTERSTITIAL_GENERATING,
      };
      setState("extendedPane", "activeArtifact", artifact);
      setState("extendedPane", "content", undefined);
    },

    clearExtendedPaneArtifact: () => {
      setState("extendedPane", "activeArtifact", undefined);
    },

    // Prompt bar actions
    setPromptBarMode: (mode: PromptBarMode) => {
      setState("promptBar", "mode", mode);
    },

    setInteractionMode: (mode: InteractionMode) => {
      setState("promptBar", "interactionMode", mode);
    },

    toggleKnowledgeSource: (source: keyof PromptBarState["knowledgeSources"]) => {
      setState("promptBar", "knowledgeSources", source, !state.promptBar.knowledgeSources[source]);
    },

    resetPromptBar: () => {
      setState("promptBar", DEFAULT_STATE.promptBar);
    },

    // Debug actions
    toggleDebugMode: () => {
      setState("isDebugMode", !state.isDebugMode);
    },

    toggleRichTextReplacement: () => {
      setState("useRichTextReplacement", !state.useRichTextReplacement);
    },

    toggleAutoCollapseOperations: () => {
      setState("enableAutoCollapseOperations", !state.enableAutoCollapseOperations);
    },

    openSecondaryPane: (pane: "assets" | "concepts") => {
      setState("navigationPane", "secondaryPane", pane);
    },
    closeSecondaryPane: () => {
      setState("navigationPane", "secondaryPane", null);
    },
  };

  const contextValue: AppUIContextType = {
    state,
    isHydrated,
    actions,
  };

  return <AppUIContext.Provider value={contextValue}>{props.children}</AppUIContext.Provider>;
};

export const useAppUI = (): AppUIContextType => {
  const context = useContext(AppUIContext);
  if (!context) {
    throw new Error("useAppUI must be used within an AppUIContextProvider");
  }
  return context;
};
